import React, { useState } from "react";
import { EllipsisVertical, Pencil, Trash2, RotateCcw, Settings2, CircleCheck } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { QuizEditModal, type QuizEditData } from "./QuizEditModal";
import type { Quiz } from "@/features/quizzes/types";

interface QuizListItemProps {
  quiz: Quiz;
  onEdit: (quizId: string, title: string) => void;
  onDelete: (quizId: string) => void;
  onRestart: (quizId: string) => void;
  onClick: (quizId: string) => void;
  onRegenerate?: (quizId: string, data: QuizEditData) => void;
  isRegenerating?: boolean;
}

export const QuizListItem: React.FC<QuizListItemProps> = ({
  quiz,
  onEdit,
  onDelete,
  onRestart,
  onClick,
  onRegenerate,
  isRegenerating = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(quiz.title);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleSaveEdit = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (!editTitle.trim()) return;
    onEdit(quiz.id, editTitle.trim());
    setIsEditing(false);
  };

  const handleCancelEdit = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditTitle(quiz.title);
  };

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditTitle(quiz.title);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(quiz.id);
  };

  const handleRestart = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRestart(quiz.id);
  };

  const handleOpenSettings = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSettingsOpen(true);
  };

  const handleRegenerate = (data: QuizEditData) => {
    setSettingsOpen(false);
    onRegenerate?.(quiz.id, data);
  };

  const scorePercent = quiz.last_score ?? 0;

  return (
    <>
      <Card
        className="group relative cursor-pointer rounded-2xl border border-primary/10 bg-card p-4 shadow-sm transition-all duration-200 hover:bg-accent/40"
        onClick={() => !isEditing && onClick(quiz.id)}
      >
        <div className="space-y-2">
          {/* Title Row */}
          <div className="flex min-w-0 items-center justify-between gap-4">
            {isEditing ? (
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveEdit(e);
                  if (e.key === "Escape") handleCancelEdit(e);
                }}
                className="h-8 text-sm"
                autoFocus
              />
            ) : (
              <span className="truncate text-base font-medium capitalize text-foreground">
                {quiz.title || "Untitled Quiz"}
              </span>
            )}

            <div className="flex flex-shrink-0 items-center gap-1">
              {scorePercent === 100 && (
                <CircleCheck className="h-5 w-5 text-correct" />
              )}
              {isEditing ? (
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={handleSaveEdit}>
                    Save
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                    >
                      <EllipsisVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent align="end" className="z-50 bg-popover">
                    <DropdownMenuItem onClick={handleStartEdit}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Rename
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={handleOpenSettings}>
                      <Settings2 className="mr-2 h-4 w-4" />
                      Edit Quiz
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    <DropdownMenuItem onClick={handleRestart}>
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Restart
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    <DropdownMenuItem onClick={handleDelete} className="text-destructive focus:text-destructive">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          {/* Info badges */}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-primary/10 px-2 py-1 text-primary">{quiz.question_count} questions</span>
            <span className="rounded-full bg-muted px-2 py-1 text-muted-foreground capitalize">{quiz.difficulty}</span>
            {quiz.status === "generating" && (
              <span className="rounded-full bg-warning/15 px-2 py-1 text-warning">
                Generating...
              </span>
            )}
          </div>

          {/* Progress bar - only show if attempted */}
          {quiz.last_attempted_at && (
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-3">
                <div className="relative h-2 w-full overflow-hidden rounded-full bg-primary/5 dark:bg-primary/10">
                  <div
                    className="h-full rounded-full bg-correct transition-all duration-300"
                    style={{
                      width: `${scorePercent}%`,
                    }}
                  />
                </div>
                <span className="text-xs font-medium text-muted-foreground">{scorePercent.toFixed(0)}%</span>
              </div>
            </div>
          )}
        </div>
      </Card>

      <QuizEditModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        quiz={quiz}
        onSave={handleRegenerate}
        isLoading={isRegenerating}
      />
    </>
  );
};
